import { useEffect, useState } from "react";
import { BodyDiagram } from "../components/BodyDiagram";
import { RecentAtSitePanel } from "../components/RecentAtSitePanel";
import { Card } from "../components/ui";
import { siteLabel } from "../lib/injections";
import { supabase } from "../supabaseClient";
import { S, css } from "../theme";

function InjectionSites({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [hoverSite, setHoverSite] = useState(null);

  useEffect(() => {
    async function load() {
      if (!user) { setLoading(false); return; }
      const { data } = await supabase.from("injection_sites").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      setLogs(data || []);
      setLoading(false);
    }
    load();
  }, [user]);

  const activeSite = hoverSite || selectedSite;

  return (
    <div style={{ padding: 40 }} className="tmp-page-pad">
      <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", marginBottom: 8 }}>Injection Sites</h2>
      <p style={{ color: S.muted, fontSize: "0.85rem", marginBottom: 24 }}>Your rotation at a glance — colors show how recently each site was used. Injections are logged automatically when you check off an injectable dose on the <strong style={{ color: S.ink2 }}>Track</strong> page. Hover or tap a site to see its recent history.</p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }} className="tmp-dash-top">
        <Card>
          <div style={{ display: "flex", gap: 14, justifyContent: "center" }}>
            <BodyDiagram view="front" logs={logs} selectedSite={selectedSite} onSelectSite={setSelectedSite} hoverSite={hoverSite} onHoverSite={setHoverSite} maxWidth={290} />
            <BodyDiagram view="back" logs={logs} selectedSite={selectedSite} onSelectSite={setSelectedSite} hoverSite={hoverSite} onHoverSite={setHoverSite} maxWidth={290} />
          </div>
          {/* Legend */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", marginTop: 16 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.7rem", color: S.muted }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: S.accent }} /> Rested (use)</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.7rem", color: S.muted }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: "#c9a227" }} /> 3–6 days</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.7rem", color: S.muted }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: S.accent2 }} /> Recent (avoid)</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.7rem", color: S.muted }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: S.surface2, border: `1px solid ${S.border}` }} /> Unused</span>
          </div>
        </Card>

        <div>
          {/* Recent at the hovered/selected site */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.muted, marginBottom: 14 }}>Recent at this site</div>
            {activeSite ? (
              <RecentAtSitePanel logs={logs} siteId={activeSite} />
            ) : (
              <p style={{ fontSize: "0.85rem", color: S.muted, lineHeight: 1.7, margin: 0 }}>Hover or tap a site on the body diagram to see its recent injections.</p>
            )}
          </Card>

          {/* Recent history (all sites) */}
          <Card>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: S.muted, marginBottom: 14 }}>Recent Injections</div>
            {loading ? (
              <p style={{ fontSize: "0.82rem", color: S.muted }}>Loading…</p>
            ) : logs.length === 0 ? (
              <p style={{ fontSize: "0.82rem", color: S.muted, margin: 0 }}>No injections logged yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {logs.slice(0, 8).map(l => (
                  <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.82rem", padding: "8px 0", borderBottom: `1px solid ${S.border}` }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: S.accent, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: S.ink2 }}>{siteLabel(l.site)}</span>
                    {l.peptide && <span style={{ color: S.accent2, fontWeight: 600 }}>{l.peptide}</span>}
                    <span style={{ marginLeft: "auto", color: S.muted, fontSize: "0.74rem" }}>{new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── BIOMARKERS ──────────────────────────────────────────────

export { InjectionSites };
